import React, { useState } from 'react';
import { Instagram, Facebook, Twitter, Linkedin, Youtube, Mail, ExternalLink, Heart, MessageCircle, Share2 } from 'lucide-react';
import JourneyHeader from './JourneyHeader';
import Footer from './Footer';

const SocialMediaHub = () => {
  const socialPlatforms = [
    { 
      name: 'Instagram', 
      icon: Instagram, 
      handle: '@inspiyahculture', 
      followers: '10K+',
      posts: '250+',
      color: 'from-purple-500 to-pink-500',
      url: '#',
      description: 'Daily inspiration, behind-the-scenes, and spiritual fashion'
    },
    { 
      name: 'Facebook', 
      icon: Facebook, 
      handle: 'InspiyahCulture', 
      followers: '8K+',
      posts: '180+',
      color: 'from-blue-600 to-blue-400',
      url: '#',
      description: 'Community discussions and event updates'
    },
    { 
      name: 'Twitter', 
      icon: Twitter, 
      handle: '@inspiyah', 
      followers: '5K+',
      posts: '420+',
      color: 'from-sky-500 to-blue-500',
      url: '#',
      description: 'Quick thoughts, wisdom drops, and conversations'
    },
    { 
      name: 'LinkedIn', 
      icon: Linkedin, 
      handle: 'INSPIYAH', 
      followers: '3K+',
      posts: '95+',
      color: 'from-blue-700 to-blue-500',
      url: '#',
      description: 'Professional insights and business transformation'
    },
    { 
      name: 'YouTube', 
      icon: Youtube, 
      handle: 'InspiyahJourney', 
      followers: '12K+',
      posts: '60+',
      color: 'from-red-600 to-red-400',
      url: '#',
      description: 'Video content, tutorials, and spiritual teachings'
    }
  ];

  const feedPosts = [
    {
      id: 1,
      platform: 'Instagram',
      image: 'https://images.unsplash.com/photo-1528715471579-d1bcf0ba5e83?w=800&h=800&fit=crop',
      caption: 'The 1010 portal opens today. Are you ready to step through? ✨🔥',
      likes: 523,
      comments: 48,
      timestamp: '2 hours ago'
    },
    {
      id: 2,
      platform: 'Twitter',
      text: 'YAH HEALTH WEALTH HAPPINESS isn\'t just a motto—it\'s a lifestyle. Choose it daily. 🙏💎',
      likes: 342,
      retweets: 89,
      timestamp: '5 hours ago'
    },
    {
      id: 3,
      platform: 'YouTube',
      thumbnail: 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=800&h=450&fit=crop',
      title: 'Building Your Business with A.R.T. ENERGI | Full Workshop',
      views: '1.2K',
      duration: '28:45',
      timestamp: '1 day ago'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      <JourneyHeader activeSection="social" />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <h1 className="text-6xl md:text-7xl font-bold bg-gradient-to-r from-brand-deep via-fire-main to-sacred-teal bg-clip-text text-transparent mb-6">
            Connect With Us
          </h1>
          <p className="text-2xl text-gray-600 max-w-3xl mx-auto mb-8">
            Join our vibrant community across platforms. Share your journey, find inspiration.
          </p>
        </div>
      </section>

      {/* Social Platform Cards */}
      <section className="pb-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {socialPlatforms.map((platform, index) => (
              <a
                key={index}
                href={platform.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
              >
                <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${platform.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <platform.icon size={40} className="text-white" />
                </div>
                <h3 className="text-3xl font-bold mb-2 text-gray-900">{platform.name}</h3>
                <p className="text-brand-deep font-semibold mb-2">{platform.handle}</p>
                <p className="text-gray-600 text-sm mb-6">{platform.description}</p>
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <div className="text-2xl font-bold text-gray-900">{platform.followers}</div>
                    <div className="text-sm text-gray-500">Followers</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900">{platform.posts}</div>
                    <div className="text-sm text-gray-500">Posts</div>
                  </div>
                </div>
                <div className="flex items-center text-brand-deep font-semibold group-hover:text-fire-main transition-colors">
                  <span>Follow Us</span>
                  <ExternalLink size={18} className="ml-2 group-hover:translate-x-1 transition-transform" />
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Social Feed Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <h2 className="text-4xl font-bold text-center mb-12 bg-gradient-to-r from-brand-deep to-fire-main bg-clip-text text-transparent">
            Latest from Our Community
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {feedPosts.map((post) => (
              <div key={post.id} className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-2xl transition-shadow duration-300">
                {post.image && (
                  <div className="relative h-64 overflow-hidden">
                    <img src={post.image} alt="" className="w-full h-full object-cover" />
                  </div>
                )}
                {post.thumbnail && (
                  <div className="relative h-64 overflow-hidden group cursor-pointer">
                    <img src={post.thumbnail} alt="" className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                      <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center">
                        <Youtube size={32} className="text-red-600" />
                      </div>
                    </div>
                    <div className="absolute bottom-3 right-3 px-2 py-1 bg-black/80 text-white text-xs rounded">
                      {post.duration}
                    </div>
                  </div>
                )}
                <div className="p-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-semibold text-brand-deep">{post.platform}</span>
                    <span className="text-sm text-gray-500">{post.timestamp}</span>
                  </div>
                  {post.caption && <p className="text-gray-700 mb-4">{post.caption}</p>}
                  {post.text && <p className="text-gray-700 mb-4">{post.text}</p>}
                  {post.title && <h3 className="text-lg font-bold text-gray-900 mb-4">{post.title}</h3>}
                  <div className="flex items-center space-x-6 text-gray-500">
                    {post.likes && (
                      <div className="flex items-center space-x-1">
                        <Heart size={18} />
                        <span className="text-sm">{post.likes}</span>
                      </div>
                    )}
                    {post.comments && (
                      <div className="flex items-center space-x-1">
                        <MessageCircle size={18} />
                        <span className="text-sm">{post.comments}</span>
                      </div>
                    )}
                    {post.views && (
                      <div className="text-sm">{post.views} views</div>
                    )}
                    <button className="ml-auto hover:text-brand-deep transition-colors">
                      <Share2 size={18} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-16 bg-gradient-to-r from-brand-deep to-fire-main">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center text-white">
          <Mail size={64} className="mx-auto mb-6" />
          <h2 className="text-4xl font-bold mb-4">Stay Inspired</h2>
          <p className="text-xl text-white/90 mb-8">
            Subscribe to receive weekly inspiration, exclusive content, and updates on new releases.
          </p>
          <form className="flex flex-col sm:flex-row gap-4 max-w-xl mx-auto">
            <input 
              type="email" 
              placeholder="Enter your email"
              className="flex-1 px-6 py-4 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white"
            />
            <button 
              type="submit"
              className="px-8 py-4 bg-white text-brand-deep font-semibold rounded-full hover:bg-brand-gold hover:text-white transition-all duration-300 hover:shadow-lg"
            >
              Subscribe
            </button>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SocialMediaHub;